import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Building2, Smartphone, Monitor, Users, Palette, Camera,
  CheckCircle, Loader2, ArrowRight, ChevronDown, MapPin
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PopupConfigurator = () => {
  const { t, language } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [estimatedSetup, setEstimatedSetup] = useState('');
  
  const [config, setConfig] = useState({
    // Establishment
    establishment_name: '',
    establishment_type: 'hotel',
    location: '',
    rooms_count: '',
    // Solution
    solution_type: 'app_only',
    // Staffing
    staffing_option: 'ifotolife_photographers',
    // Pricing Model
    pricing_model: 'commission', // 'commission' or 'rental'
    // Branding
    branding_custom: false,
    backdrop_style: 'classic',
    // Contact
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    // Notes
    additional_notes: ''
  });

  const establishmentTypes = [
    { value: 'hotel', label: t('popup.configurator.establishment.types.hotel'), icon: Building2 },
    { value: 'resort', label: t('popup.configurator.establishment.types.resort'), icon: Building2 },
    { value: 'camping', label: t('popup.configurator.establishment.types.camping'), icon: Building2 },
    { value: 'event', label: t('popup.configurator.establishment.types.event'), icon: Building2 }
  ];

  const solutionTypes = [
    { value: 'app_only', label: t('popup.configurator.solution.appOnly'), icon: Smartphone, price: 'Inclus' },
    { value: 'kiosk', label: t('popup.configurator.solution.kiosk'), icon: Monitor, price: 'Sur devis' },
    { value: 'both', label: t('popup.configurator.solution.both'), icon: Camera, price: 'Premium' }
  ];

  const staffingOptions = [
    { value: 'ifotolife_photographers', label: t('popup.configurator.staffing.ifotolife') },
    { value: 'own_team', label: t('popup.configurator.staffing.ownTeam') },
    { value: 'mixed', label: t('popup.configurator.staffing.mixed') }
  ];

  const pricingModels = [
    { value: 'commission', label: language === 'fr' ? 'Commission (20% reversé)' : 'Commission (20% paid back)', description: language === 'fr' ? 'Vous recevez 20% sur chaque vente' : 'You receive 20% on each sale' },
    { value: 'rental', label: language === 'fr' ? 'Loyer fixe (payé par iFotoLife)' : 'Fixed rent (paid by iFotoLife)', description: language === 'fr' ? 'iFotoLife vous verse un loyer mensuel' : 'iFotoLife pays you a monthly rent' }
  ];

  const backdropStyles = [
    { value: 'classic', label: 'Classique', color: '#1a1a1a' },
    { value: 'tropical', label: 'Tropical', color: '#2d5a27' },
    { value: 'beach', label: 'Plage', color: '#87CEEB' },
    { value: 'luxury', label: 'Luxe', color: '#8B4513' },
    { value: 'custom', label: 'Personnalisé', color: '#FF7A00' }
  ];

  const handleChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        ...config,
        rooms_count: config.rooms_count ? parseInt(config.rooms_count) : null,
        language
      };

      const response = await axios.post(`${BACKEND_URL}/api/popup-installation`, payload);

      if (response.data.success) {
        setIsSubmitted(true);
        setEstimatedSetup(response.data.estimated_setup);
        toast.success(t('popup.configurator.success'));
      }
    } catch (error) {
      console.error('Installation request error:', error);
      toast.error(t('popup.configurator.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Preview component
  const ConfigPreview = () => (
    <div className="relative w-full aspect-[3/4] rounded-2xl overflow-hidden bg-[#121212] border border-white/10">
      {/* Backdrop Preview */}
      <div 
        className="absolute inset-0 transition-colors duration-500"
        style={{ 
          backgroundColor: backdropStyles.find(b => b.value === config.backdrop_style)?.color || '#1a1a1a',
          opacity: 0.3
        }}
      />
      
      {/* Content */}
      <div className="relative h-full flex flex-col items-center justify-center p-6 text-center">
        {/* Logo Area */}
        {config.branding_custom && (
          <div className="absolute top-4 left-4 right-4">
            <div className="w-full h-12 rounded-lg bg-white/10 border border-dashed border-white/20 flex items-center justify-center">
              <span className="text-xs text-white/40">Votre Logo</span>
            </div>
          </div>
        )}
        
        {/* Main Preview */}
        <div className="flex flex-col items-center">
          <div className="w-24 h-24 rounded-2xl bg-[#FF7A00]/10 border border-[#FF7A00]/30 flex items-center justify-center mb-4">
            {config.solution_type === 'app_only' && <Smartphone className="w-10 h-10 text-[#FF7A00]" />}
            {config.solution_type === 'kiosk' && <Monitor className="w-10 h-10 text-[#FF7A00]" />}
            {config.solution_type === 'both' && <Camera className="w-10 h-10 text-[#FF7A00]" />}
          </div>
          
          <h4 className="text-xl font-semibold text-white mb-2">
            {config.establishment_name || 'Votre Établissement'}
          </h4>
          <p className="text-sm text-white/50 mb-4">
            {establishmentTypes.find(t => t.value === config.establishment_type)?.label}
          </p>
          
          {config.location && (
            <div className="flex items-center gap-1 text-sm text-white/40">
              <MapPin size={14} />
              {config.location}
            </div>
          )}
        </div>

        {/* Solution Badge */}
        <div className="absolute bottom-6 left-6 right-6">
          <div className="glass rounded-xl p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-white/70">Solution</span>
              <span className="text-sm font-semibold text-[#FF7A00]">
                {solutionTypes.find(s => s.value === config.solution_type)?.label}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="glass-card rounded-3xl p-12 text-center max-w-lg mx-auto"
        data-testid="configurator-success"
      >
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#10B981]/10 flex items-center justify-center">
          <CheckCircle className="w-10 h-10 text-[#10B981]" />
        </div>
        <h3 className="text-3xl font-bold text-white mb-4">Demande Envoyée !</h3>
        <p className="text-white/60 mb-6">{t('popup.configurator.success')}</p>
        
        <div className="glass rounded-xl p-4 mb-6">
          <p className="text-sm text-white/50 mb-1">Délai d'installation estimé</p>
          <p className="text-2xl font-bold text-[#FF7A00]">{estimatedSetup}</p>
        </div>
        
        <p className="text-sm text-white/40">
          Notre équipe vous contactera dans les 48h pour planifier l'installation.
        </p>
      </motion.div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
      {/* Left - Preview */}
      <motion.div
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6 }}
        className="order-2 lg:order-1"
      >
        <div className="sticky top-28">
          <h3 className="text-lg font-semibold text-white mb-4">Aperçu de votre installation</h3>
          <ConfigPreview />
        </div>
      </motion.div>

      {/* Right - Form */}
      <motion.div
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="order-1 lg:order-2"
      >
        <form onSubmit={handleSubmit} data-testid="configurator-form">
          <Accordion type="single" collapsible defaultValue="establishment" className="space-y-4">
            {/* Establishment */}
            <AccordionItem value="establishment" className="glass-card rounded-2xl border-0 overflow-hidden">
              <AccordionTrigger className="px-6 py-4 hover:no-underline" data-testid="accordion-establishment">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#FF7A00]/10 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-[#FF7A00]" />
                  </div>
                  <span className="font-semibold text-white">{t('popup.configurator.establishment.title')}</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-white/70 mb-2">{t('popup.configurator.establishment.name')} *</label>
                    <input
                      type="text"
                      value={config.establishment_name}
                      onChange={(e) => handleChange('establishment_name', e.target.value)}
                      required
                      data-testid="config-establishment-name"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-[#FF7A00] transition-colors"
                      placeholder="Ex: Hôtel Royal Palm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-white/70 mb-2">{t('popup.configurator.establishment.type')}</label>
                    <div className="grid grid-cols-2 gap-3">
                      {establishmentTypes.map((type) => (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => handleChange('establishment_type', type.value)}
                          data-testid={`config-type-${type.value}`}
                          className={`p-3 rounded-xl border transition-all text-left ${
                            config.establishment_type === type.value
                              ? 'bg-[#FF7A00]/10 border-[#FF7A00]/50 text-white'
                              : 'bg-white/5 border-white/10 text-white/70 hover:border-white/20'
                          }`}
                        >
                          <span className="text-sm font-medium">{type.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-white/70 mb-2">{t('popup.configurator.establishment.location')}</label>
                      <input
                        type="text"
                        value={config.location}
                        onChange={(e) => handleChange('location', e.target.value)}
                        data-testid="config-location"
                        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-[#FF7A00] transition-colors"
                        placeholder="Ex: Marrakech"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-white/70 mb-2">{t('popup.configurator.establishment.rooms')}</label>
                      <input
                        type="number"
                        value={config.rooms_count}
                        onChange={(e) => handleChange('rooms_count', e.target.value)}
                        data-testid="config-rooms"
                        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-[#FF7A00] transition-colors"
                        placeholder="Ex: 150"
                      />
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Solution */}
            <AccordionItem value="solution" className="glass-card rounded-2xl border-0 overflow-hidden">
              <AccordionTrigger className="px-6 py-4 hover:no-underline" data-testid="accordion-solution">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#FF7A00]/10 flex items-center justify-center">
                    <Smartphone className="w-5 h-5 text-[#FF7A00]" />
                  </div>
                  <span className="font-semibold text-white">{t('popup.configurator.solution.title')}</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6">
                <div className="space-y-3">
                  {solutionTypes.map((solution) => {
                    const Icon = solution.icon;
                    return (
                      <button
                        key={solution.value}
                        type="button"
                        onClick={() => handleChange('solution_type', solution.value)}
                        data-testid={`config-solution-${solution.value}`}
                        className={`w-full p-4 rounded-xl border transition-all flex items-center justify-between ${
                          config.solution_type === solution.value
                            ? 'bg-[#FF7A00]/10 border-[#FF7A00]/50'
                            : 'bg-white/5 border-white/10 hover:border-white/20'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Icon className={`w-5 h-5 ${config.solution_type === solution.value ? 'text-[#FF7A00]' : 'text-white/50'}`} />
                          <span className={config.solution_type === solution.value ? 'text-white' : 'text-white/70'}>
                            {solution.label}
                          </span>
                        </div>
                        <span className={`text-sm ${config.solution_type === solution.value ? 'text-[#FF7A00]' : 'text-white/40'}`}>
                          {solution.price}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Staffing */}
            <AccordionItem value="staffing" className="glass-card rounded-2xl border-0 overflow-hidden">
              <AccordionTrigger className="px-6 py-4 hover:no-underline" data-testid="accordion-staffing">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#FF7A00]/10 flex items-center justify-center">
                    <Users className="w-5 h-5 text-[#FF7A00]" />
                  </div>
                  <span className="font-semibold text-white">{t('popup.configurator.staffing.title')}</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6">
                <div className="space-y-3">
                  {staffingOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleChange('staffing_option', option.value)}
                      data-testid={`config-staffing-${option.value}`}
                      className={`w-full p-4 rounded-xl border transition-all text-left ${
                        config.staffing_option === option.value
                          ? 'bg-[#FF7A00]/10 border-[#FF7A00]/50 text-white'
                          : 'bg-white/5 border-white/10 text-white/70 hover:border-white/20'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Pricing Model */}
            <AccordionItem value="pricing" className="glass-card rounded-2xl border-0 overflow-hidden">
              <AccordionTrigger className="px-6 py-4 hover:no-underline" data-testid="accordion-pricing">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#FF7A00]/10 flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#FF7A00]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <span className="font-semibold text-white">{language === 'fr' ? 'Modèle de tarification' : 'Pricing Model'}</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6">
                <div className="space-y-3">
                  {pricingModels.map((model) => (
                    <button
                      key={model.value}
                      type="button"
                      onClick={() => handleChange('pricing_model', model.value)}
                      data-testid={`config-pricing-${model.value}`}
                      className={`w-full p-4 rounded-xl border transition-all text-left ${
                        config.pricing_model === model.value
                          ? model.value === 'rental' 
                            ? 'bg-[#10B981]/10 border-[#10B981]/50 text-white'
                            : 'bg-[#FF7A00]/10 border-[#FF7A00]/50 text-white'
                          : 'bg-white/5 border-white/10 text-white/70 hover:border-white/20'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{model.label}</span>
                        {config.pricing_model === model.value && (
                          <CheckCircle className={`w-5 h-5 ${model.value === 'rental' ? 'text-[#10B981]' : 'text-[#FF7A00]'}`} />
                        )}
                      </div>
                      <p className="text-sm text-white/50 mt-1">{model.description}</p>
                    </button>
                  ))}
                </div>
                {config.staffing_option === 'ifotolife_photographers' && config.pricing_model === 'rental' && (
                  <div className="mt-4 p-4 rounded-xl bg-[#10B981]/10 border border-[#10B981]/30">
                    <p className="text-sm text-[#10B981]">
                      {language === 'fr' 
                        ? '✓ Avec l\'équipe iFotoLife et l\'option loyer, vous recevez un loyer mensuel fixe pour l\'emplacement du stand.'
                        : '✓ With the iFotoLife team and rental option, you receive a fixed monthly rent for the stand location.'}
                    </p>
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>

            {/* Branding */}
            <AccordionItem value="branding" className="glass-card rounded-2xl border-0 overflow-hidden">
              <AccordionTrigger className="px-6 py-4 hover:no-underline" data-testid="accordion-branding">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#FF7A00]/10 flex items-center justify-center">
                    <Palette className="w-5 h-5 text-[#FF7A00]" />
                  </div>
                  <span className="font-semibold text-white">{t('popup.configurator.branding.title')}</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6">
                <div className="space-y-4">
                  {/* Custom Branding Toggle */}
                  <label className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10 cursor-pointer hover:border-white/20 transition-colors">
                    <span className="text-white/70">{t('popup.configurator.branding.custom')}</span>
                    <input
                      type="checkbox"
                      checked={config.branding_custom}
                      onChange={(e) => handleChange('branding_custom', e.target.checked)}
                      data-testid="config-branding-custom"
                      className="w-5 h-5 rounded accent-[#FF7A00]"
                    />
                  </label>

                  {/* Backdrop Style */}
                  <div>
                    <label className="block text-sm text-white/70 mb-3">{t('popup.configurator.branding.backdrop')}</label>
                    <div className="flex flex-wrap gap-3">
                      {backdropStyles.map((backdrop) => (
                        <button
                          key={backdrop.value}
                          type="button"
                          onClick={() => handleChange('backdrop_style', backdrop.value)}
                          data-testid={`config-backdrop-${backdrop.value}`}
                          className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-all ${
                            config.backdrop_style === backdrop.value
                              ? 'bg-white/10 border-[#FF7A00]/50'
                              : 'bg-white/5 border-white/10 hover:border-white/20'
                          }`}
                        >
                          <div 
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: backdrop.color }}
                          />
                          <span className={`text-sm ${config.backdrop_style === backdrop.value ? 'text-white' : 'text-white/60'}`}>
                            {backdrop.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Contact */}
            <AccordionItem value="contact" className="glass-card rounded-2xl border-0 overflow-hidden">
              <AccordionTrigger className="px-6 py-4 hover:no-underline" data-testid="accordion-contact">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#FF7A00]/10 flex items-center justify-center">
                    <Users className="w-5 h-5 text-[#FF7A00]" />
                  </div>
                  <span className="font-semibold text-white">{t('popup.configurator.contact.title')}</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-white/70 mb-2">{t('popup.configurator.contact.name')} *</label>
                    <input
                      type="text"
                      value={config.contact_name}
                      onChange={(e) => handleChange('contact_name', e.target.value)}
                      required
                      data-testid="config-contact-name"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-[#FF7A00] transition-colors"
                      placeholder="Votre nom"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-white/70 mb-2">{t('popup.configurator.contact.email')} *</label>
                      <input
                        type="email"
                        value={config.contact_email}
                        onChange={(e) => handleChange('contact_email', e.target.value)}
                        required
                        data-testid="config-contact-email"
                        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-[#FF7A00] transition-colors"
                        placeholder="email@hotel.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-white/70 mb-2">{t('popup.configurator.contact.phone')}</label>
                      <input
                        type="tel"
                        value={config.contact_phone}
                        onChange={(e) => handleChange('contact_phone', e.target.value)}
                        data-testid="config-contact-phone"
                        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-[#FF7A00] transition-colors"
                        placeholder="+212 6 00 00 00 00"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-white/70 mb-2">{t('popup.configurator.notes')}</label>
                    <textarea
                      value={config.additional_notes}
                      onChange={(e) => handleChange('additional_notes', e.target.value)}
                      rows={3}
                      data-testid="config-notes"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-[#FF7A00] transition-colors resize-none"
                      placeholder="Informations supplémentaires..."
                    />
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Submit Button */}
          <motion.button
            type="submit"
            disabled={isSubmitting}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="config-submit-btn"
            className="w-full mt-6 btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Envoi en cours...
              </>
            ) : (
              <>
                {t('popup.configurator.submit')}
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </motion.button>
        </form>
      </motion.div>
    </div>
  );
};

export default PopupConfigurator;
